import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Button, ButtonProps } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ConfirmModal,
  DrawerComponent,
  ResolutionChip,
  dateColumn,
  tableStyleOverrides,
  textColumn,
} from '@dispatch/web/ui';
import { ResultStatus, TaskInfo, TaskType } from '@dispatch/shared/types';
import { getTimeWithOffset } from '@dispatch/shared';
import { isRunningInJest } from '@dispatch/testing';
import { GeoStatusChip } from './GeoStatusChip';

const EMPTY_DATETIMEOFFSET = '0001-01-01T00:00:00+00:00';
const CANCEL_ROUTE = '/tasks';

type DrawerRouteState = {
  height: string;
  width: string;
  pagination: { pageSize: number; pagingOptions: number[] };
  rowHeight: number;
  rows: TaskInfo[];
  loading: boolean;
};

type ModalType = 'bulkCancelTask' | 'bulkLockTask';

export type BulkTaskActionDrawerProps = {
  title: string;
  actionButton: {
    label: string;
    color: ButtonProps['color'];
    testId: string;
  };
  modalType: ModalType;
  noRowsMessage: string;
  filterRow: (row: TaskInfo) => boolean;
  renderConfirm: (selectedCount: number) => React.ReactElement;
  confirmTitle: string | ((selectedCount: number) => string);
  actionStatus: ResultStatus;
  onConfirm: (selectedRows: TaskInfo[]) => void;
  onModalDeny?: () => void;
};

const buildGridColumns = (): GridColDef[] => [
  textColumn({ field: 'claimNumber', headerName: 'Claim Number', flex: 2 }),
  textColumn({ field: 'insuredName', headerName: 'Insured Name', flex: 2 }),
  textColumn({ field: 'location', headerName: 'Location' }),
  dateColumn({
    field: 'appointment',
    headerName: 'Appointment',
    flex: 1,
    valueGetter: (params) =>
      !!params?.row?.appointment &&
      params?.row?.appointment !== EMPTY_DATETIMEOFFSET &&
      params?.row?.taskType !== TaskType.CLAIM_OWNER
        ? getTimeWithOffset(params?.row?.timeZoneId, params?.row?.appointment)
        : '-',
  }),
  {
    field: 'targetQueue',
    headerName: 'Status',
    renderCell: (params) => {
      const row = params?.row;
      const tooltipString =
        !row.lossLat || !row.lossLong ? 'Address is Incorrect' : '';
      return (
        <Stack direction="row" gap={1}>
          <ResolutionChip
            targetQueue={params.value}
            tooltipString={tooltipString}
          />
          <GeoStatusChip geoStatus={row.geoStatus} showIcon size="small" />
        </Stack>
      );
    },
    flex: 0.5,
    minWidth: 200,
    sortable: false,
  },
];

export const BulkTaskActionDrawer = ({
  title,
  actionButton,
  modalType,
  noRowsMessage,
  filterRow,
  renderConfirm,
  confirmTitle,
  actionStatus,
  onConfirm,
  onModalDeny,
}: BulkTaskActionDrawerProps) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: DrawerRouteState };
  const { height, width, pagination, rowHeight, rows, loading } = state || {};

  useEffect(() => {
    if (!state) {
      navigate(CANCEL_ROUTE, { replace: true });
    }
  }, [state, navigate]);

  const filteredRows = useMemo(
    () => (rows || []).filter(filterRow),
    [rows, filterRow]
  );

  const [selectedTaskIds, setSelectedTaskIds] = useState<GridRowId[]>([]);
  const [selectedRows, setSelectedRows] = useState<TaskInfo[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const columns = useMemo(() => buildGridColumns(), []);

  const handleActionClick = () => setShowConfirm(true);
  const handleConfirm = () => onConfirm(selectedRows);
  const handleDeny = () => {
    setShowConfirm(false);
    onModalDeny?.();
  };

  const resolvedConfirmTitle =
    typeof confirmTitle === 'function'
      ? confirmTitle(selectedTaskIds.length)
      : confirmTitle;

  const actionButtonEl = (
    <Stack px={1}>
      <Button
        data-testid={actionButton.testId}
        type="button"
        variant="outlined"
        color={actionButton.color}
        onClick={handleActionClick}
        disabled={selectedTaskIds.length === 0}
        sx={{
          fontWeight: '700',
          lineHeight: '1.3750em',
          letterSpacing: '0.029em',
        }}
      >
        {actionButton.label}
      </Button>
    </Stack>
  );

  return (
    <div>
      <DrawerComponent
        onClose={() => navigate(CANCEL_ROUTE)}
        renderLeftSideButtons={[actionButtonEl]}
        showSaveButton={false}
        title={title}
        showButtons={true}
        saveDisabled={selectedTaskIds.length === 0}
        sx={{
          width: '65vw',
          '& .MuiBox-root': {
            padding: 0,
          },
        }}
      >
        <div style={{ height, width }}>
          <DataGridPro
            loading={loading}
            disableVirtualization={isRunningInJest()}
            onSelectionModelChange={(newSelectionModel) => {
              const selectedIds = Array.isArray(newSelectionModel)
                ? newSelectionModel
                : [newSelectionModel];
              setSelectedTaskIds(selectedIds);

              const selectedFullRows = rows?.filter((row: TaskInfo) =>
                selectedIds.includes(row.id)
              );
              setSelectedRows(selectedFullRows || []);
            }}
            rows={filteredRows}
            components={{
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {noRowsMessage}
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  Filter Returned No Tasks
                </Stack>
              ),
            }}
            selectionModel={selectedTaskIds}
            rowHeight={rowHeight || 32}
            columns={[...columns]}
            pageSize={pagination?.pageSize || 10}
            checkboxSelection
            sx={{ ...tableStyleOverrides }}
          />
        </div>
      </DrawerComponent>
      {showConfirm && (
        <ConfirmModal
          actionStatus={actionStatus}
          onConfirm={handleConfirm}
          onDeny={handleDeny}
          confirmContent={renderConfirm(selectedTaskIds.length)}
          confirmTitle={resolvedConfirmTitle}
          modalType={modalType}
        />
      )}
    </div>
  );
};
